import { Modal, Button, Steps, Select, Input, message } from "antd";
import { useEffect, useState } from "react";
import axios from "axios";
import { env } from "../../../../configs/EnvironmentConfig";
import { useTranslation } from "react-i18next";

const { Step } = Steps;
const { Option } = Select;

const WelcomePopup = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  const [visible, setVisible] = useState(() => {
    return localStorage.getItem("store_setup_complete") !== "true";
  });
  const [current, setCurrent] = useState(0);

  const [domains, setDomains] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [aRecord, setARecord] = useState("13.202.241.234");
  const [storeUrl, setStoreUrl] = useState("");
  const [isDomainPointed, setIsDomainPointed] = useState(false);
  const [checkingPointing, setCheckingPointing] = useState(false);
  const [storeName, setStoreName] = useState("");
  const [isCreatingStore, setIsCreatingStore] = useState(false);
  const [isCreatingFinalStore, setIsCreatingFinalStore] = useState(false);

  useEffect(() => {
    const maybeAutoAddDNS = async () => {
      if (current !== 2 || !selectedDomain) return;

      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/api/domain-manage/domain-theme/${selectedDomain}`
        );

        const domainDoc = res.data?.domain;

        if (!domainDoc) {
          return console.warn("No domain theme found for:", selectedDomain);
        }

        if (domainDoc.isExternal === false) {
          console.log("🔧 Adding A Record automatically (non-external domain)");

          await axios.post(
            `${process.env.REACT_APP_BASE_URL}/api/domain-manage/domain/${selectedDomain}/dns`,
            {
              type: "A",
              name: "@",
              data: aRecord,
              ttl: 3600,
            }
          );

          message.success(t("wizard.store_created_success"));
        } else {
          console.log("📝 Skipping auto A record - domain is external");
        }
      } catch (err) {
        console.error("Failed to auto-add A record:", err);
        message.error(t("wizard.domain_config_failed"));
      }
    };

    maybeAutoAddDNS();
  }, [current, selectedDomain, aRecord, t]);

  const handleDomainVerify = async () => {
    if (!selectedDomain) return message.warning(t("wizard.domain_required"));

    const storeId = localStorage.getItem("store_id");
    if (!storeId) return message.error(t("wizard.missing_data"));

    try {
      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/domain-manage/validate-and-assign`,
        {
          domain: selectedDomain,
          storeId,
        }
      );

      message.success(t("wizard.store_created_success"));
      next();
    } catch (err) {
      console.error(err);
      message.error(
        err.response?.data?.error || t("wizard.domain_config_failed")
      );
    }
  };

  useEffect(() => {
    const storedId = localStorage.getItem("store_id");
    if (!storedId) {
      message.error(t("wizard.missing_data"));
      return;
    }

    const fetchDomainList = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/api/domain-manage/domains/${storedId}`
        );
        setDomains(res.data || []);

        if (res.data?.length > 0) {
          const first = res.data[0];
          setSelectedDomain(first.domain || null);
        }
      } catch (err) {
        console.error("Failed to fetch domain list:", err);
        message.error(t("wizard.domain_config_failed"));
      }
    };

    fetchDomainList();
  }, [t]);

  const next = () => {
    if (current === 1 && !selectedDomain) {
      return message.warning(t("wizard.domain_required"));
    }
    if (current === 2 && !aRecord) {
      return message.warning("Please enter A record IP.");
    }
    setCurrent((prev) => prev + 1);
  };

  const prev = () => {
    setCurrent((prev) => prev - 1);
  };

  const handleClose = () => {
    setVisible(false);
  };

  const handleCreateStore = async () => {
    const storeId = localStorage.getItem("store_id");
    const email = localStorage.getItem("email");

    if (!storeId || !selectedDomain || !email) {
      message.error(t("wizard.missing_data"));
      return;
    }

    setIsCreatingFinalStore(true);

    try {
      // Step 1: Set the A record
      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/domain-manage/domain/${selectedDomain}/dns`,
        {
          type: "A",
          name: "@",
          data: aRecord,
          ttl: 3600,
        }
      );

      // Step 2: Check pointing after A record is set
      const check = await axios.get(
        `${env.DOMAIN_URL_POINT}/dns/check-domain?domain=${selectedDomain}`
      );

      const isSuccess =
        check.data?.status?.toLowerCase() === "success" ||
        check.data?.success === true;

      if (!isSuccess) {
        message.warning(t("wizard.domain_will_take_time"));
        return;
      }

      // Step 3: Call external store creation API
      await axios.post(`${env.DOMAIN_URL_POINT}/dns/create-store`, {
        storeId,
        domain: selectedDomain,
        email,
      });

      message.success(t("wizard.store_created_success"));
      localStorage.setItem("store_setup_complete", "true");
      setStoreUrl(selectedDomain);
      next();
    } catch (err) {
      console.error("Store creation failed:", err);
      message.error(t("wizard.domain_config_failed"));
    } finally {
      setIsCreatingFinalStore(false);
    }
  };

  const createStoreInDatabase = async (storeId, storeName, domain) => {
    const token = localStorage.getItem("auth_token");
    if (!token) {
      throw new Error("Missing auth token");
    }

    const withScheme = (value, scheme = "http") =>
      /^https?:\/\//i.test(value) ? value : `${scheme}://${value}`;

    const urlHttp = withScheme(domain, "http");
    const nameForTechno = storeName || domain;

    // Read limits saved by MenuContent
    const product_limit = (() => {
      const v = Number(localStorage.getItem("product_limit"));
      return Number.isFinite(v) ? v : null;
    })();
    const user_limit = (() => {
      const v = Number(localStorage.getItem("user_limit"));
      return Number.isFinite(v) ? v : null;
    })();

    const payload = {
      store_id: storeId,
      name: storeName,
      url: urlHttp,
      settings: [
        {
          code: "config",
          key: "config_name",
          value: storeName,
          serialized: false,
        },
        {
          code: "config",
          key: "config_url",
          value: urlHttp,
          serialized: false,
        },
        {
          code: "config",
          key: "config_meta_title",
          value: storeName,
          serialized: false,
        },
      ],
    };

    const technoJson = {
      store_id: String(storeId),
      name: nameForTechno,
      url: urlHttp,
      product_limit,
      user_limit,
    };

    const [primaryResp, technoResp] = await Promise.all([
      axios.post(`${env.API_ENDPOINT_URL}/store`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      }),
      axios.post(
        `${process.env.REACT_APP_BASE_PUBLIC_STORE_URL}/store_update.php`,
        technoJson,
        { headers: { "Content-Type": "application/json" } }
      ),
    ]);

    return {
      primary: primaryResp.data,
      techno: technoResp.data,
    };
  };

  const steps = [
    {
      title: t("wizard.welcome"),
      content: (
        <div
          style={{
            direction: isRTL ? "rtl" : "ltr",
            textAlign: isRTL ? "right" : "left",
          }}
        >
          <p>{t("wizard.welcome_message")}</p>
          <p>{t("wizard.welcome_description")}</p>
          <Button type="primary" onClick={next}>
            {t("wizard.start")}
          </Button>
        </div>
      ),
    },
    {
      title: t("wizard.enter_domain"),
      content: (
        <div
          style={{
            direction: isRTL ? "rtl" : "ltr",
            textAlign: isRTL ? "right" : "left",
          }}
        >
          <p>{t("wizard.domain_description")}</p>
          <Input
            placeholder={t("wizard.domain_placeholder")}
            value={selectedDomain}
            onChange={(e) => setSelectedDomain(e.target.value)}
            style={{ textAlign: isRTL ? "right" : "left" }}
          />
          <Button
            type="primary"
            onClick={handleDomainVerify}
            style={{ marginTop: 12 }}
          >
            {t("wizard.verify_domain")}
          </Button>
        </div>
      ),
    },
    {
      title: t("wizard.add_a_record"),
      content: (
        <div
          style={{
            direction: isRTL ? "rtl" : "ltr",
            textAlign: isRTL ? "right" : "left",
          }}
        >
          <p>{t("wizard.a_record_description")}</p>
          <Input
            addonBefore={t("wizard.type")}
            value="A"
            disabled
            style={{ marginBottom: 8, textAlign: isRTL ? "right" : "left" }}
          />
          <Input
            addonBefore={t("wizard.host")}
            value="@"
            disabled
            style={{ marginBottom: 8, textAlign: isRTL ? "right" : "left" }}
          />
          <Input
            addonBefore={t("wizard.value")}
            value={aRecord}
            disabled
            style={{ marginBottom: 8, textAlign: isRTL ? "right" : "left" }}
          />
          <Input
            addonBefore={t("wizard.ttl")}
            value={30}
            disabled
            style={{ textAlign: isRTL ? "right" : "left" }}
          />
        </div>
      ),
    },
    {
      title: t("wizard.confirm_create"),
      content: (
        <div
          style={{
            direction: isRTL ? "rtl" : "ltr",
            textAlign: isRTL ? "right" : "left",
          }}
        >
          <p>{t("wizard.confirm_description")}</p>
          <Button
            type="primary"
            loading={isCreatingFinalStore}
            onClick={handleCreateStore}
          >
            {t("wizard.create_store")}
          </Button>
        </div>
      ),
    },
    {
      title: t("wizard.store_info"),
      content: (
        <div
          style={{
            direction: isRTL ? "rtl" : "ltr",
            textAlign: isRTL ? "right" : "left",
          }}
        >
          <p>{t("wizard.store_info_description")}</p>
          <p>
            <strong>{t("wizard.store_id")}:</strong>{" "}
            {localStorage.getItem("store_id")}
          </p>
          <p>
            <strong>{t("wizard.domain")}:</strong> {selectedDomain}
          </p>
          <Input
            placeholder={t("wizard.store_name_placeholder")}
            value={storeName}
            onChange={(e) => setStoreName(e.target.value)}
            style={{ marginTop: 12, textAlign: isRTL ? "right" : "left" }}
          />
          <Button
            type="primary"
            loading={isCreatingStore}
            onClick={async () => {
              const storeId = localStorage.getItem("store_id");

              if (!storeName) {
                return message.warning(t("wizard.store_name_required"));
              }
              if (!storeId || !selectedDomain) {
                return message.error(t("wizard.missing_data"));
              }

              setIsCreatingStore(true);
              try {
                await createStoreInDatabase(storeId, storeName, selectedDomain);
                message.success(t("wizard.store_saved_success"));
                next();
              } catch (err) {
                console.error("Store creation failed:", err);
                message.error(t("wizard.store_save_failed"));
              } finally {
                setIsCreatingStore(false);
              }
            }}
            style={{ marginTop: 16 }}
          >
            {t("wizard.continue")}
          </Button>
        </div>
      ),
    },
    {
      title: t("wizard.store_url"),
      content: (
        <div
          style={{
            direction: isRTL ? "rtl" : "ltr",
            textAlign: isRTL ? "right" : "left",
          }}
        >
          <p>{t("wizard.store_ready")}</p>
          <p>
            <strong>{t("wizard.url")}:</strong>{" "}
            <a
              href={`http://${storeUrl}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {storeUrl}
            </a>
          </p>
        </div>
      ),
    },
  ];

  return (
    <Modal
      open={visible}
      title={t("wizard.store_setup_title")}
      onCancel={handleClose}
      footer={null}
      width={900}
      style={{ direction: isRTL ? "rtl" : "ltr" }}
    >
      <Steps
        current={current}
        size="small"
        direction={isRTL ? "vertical" : "horizontal"}
      >
        {steps.map((item) => (
          <Step key={item.title} title={item.title} />
        ))}
      </Steps>

      <div style={{ marginTop: 24, minHeight: 100 }}>
        {steps[current].content}
      </div>

      <div
        style={{
          marginTop: 24,
          textAlign: isRTL ? "left" : "right",
          direction: isRTL ? "rtl" : "ltr",
        }}
      >
        {current > 0 && current < steps.length - 1 && (
          <Button
            style={{ margin: isRTL ? "0 0 0 8px" : "0 8px 0 0" }}
            onClick={prev}
          >
            {t("wizard.previous")}
          </Button>
        )}
        {current < steps.length - 2 && (
          <Button type="primary" onClick={next}>
            {t("wizard.next")}
          </Button>
        )}
        {current === steps.length - 1 && (
          <Button type="primary" onClick={handleClose}>
            {t("wizard.done")}
          </Button>
        )}
      </div>
    </Modal>
  );
};

export default WelcomePopup;
